import type { PageFont } from '../model/pages';

// A page font = a display family (headings/buttons) + a body family, plus the one
// Google Fonts stylesheet href that loads both. PageView renders an inline
// <link rel="stylesheet"> with this href (so it loads in the public page AND
// inside the editor's iframe preview, where children are portaled in) and sets
// --page-fd / --page-fb CSS variables that the section styles consume.
export interface FontPair {
  label: string;
  display: string; // CSS font-family value for headings + buttons
  body: string;    // CSS font-family value for body text
  href: string;    // Google Fonts css2 URL loading both families
  /** The display face ships a single regular weight: headings render at 400
   *  (never a browser-synthesized bold), whatever weight the style asks for. */
  regular?: boolean;
}

const G = (families: string) => `https://fonts.googleapis.com/css2?${families}&display=swap`;

export const FONTS: Record<PageFont, FontPair> = {
  jakarta: {
    label: 'Jakarta', display: "'Plus Jakarta Sans', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600'),
  },
  inter: {
    label: 'Inter', display: "'Inter', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Inter:wght@400;500;600;700;800'),
  },
  manrope: {
    label: 'Manrope', display: "'Manrope', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Manrope:wght@500;600;700;800&family=Inter:wght@400;500;600'),
  },
  sora: {
    label: 'Sora', display: "'Sora', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Sora:wght@500;600;700;800&family=Inter:wght@400;500;600'),
  },
  space: {
    label: 'Space Grotesk', display: "'Space Grotesk', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600'),
  },
  // A more refined grotesque than Space Grotesk for decks/headlines: Bricolage
  // Grotesque ships a real optical-size (opsz) axis tuned for display sizes,
  // so its letterforms actually sharpen and gain character at 48px+ instead
  // of just scaling up flat the way a static face like Inter does.
  bricolage: {
    label: 'Bricolage', display: "'Bricolage Grotesque', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600'),
  },
  figtree: {
    label: 'Figtree', display: "'Figtree', sans-serif", body: "'Figtree', sans-serif",
    href: G('family=DM+Serif+Display:ital@0;1&family=Figtree:wght@400;500;600;700;800'),
  },
  playfair: {
    label: 'Playfair', display: "'Playfair Display', serif", body: "'Inter', sans-serif",
    href: G('family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600'),
  },
  fraunces: {
    label: 'Fraunces', display: "'Fraunces', serif", body: "'Inter', sans-serif",
    href: G('family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600'),
  },
  cormorant: {
    label: 'Cormorant', display: "'Cormorant Garamond', serif", body: "'DM Sans', sans-serif",
    href: G('family=Cormorant+Garamond:wght@600;700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600'),
  },
  // v2.58 funnel pairings.
  // Poppins: the geometric sans most course and offer pages are set in.
  poppins: {
    label: 'Poppins', display: "'Poppins', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600'),
  },
  // Oswald: a condensed display face with real bold weights, for oversized
  // all-caps program headlines (no faux bold, unlike single-weight condensed faces).
  oswald: {
    label: 'Oswald', display: "'Oswald', sans-serif", body: "'Inter', sans-serif",
    href: G('family=Oswald:wght@500;600;700&family=Inter:wght@400;500;600'),
  },
  // Instrument Serif: a modern editorial display serif (one weight + italic).
  // `regular` makes headings use its real 400 weight instead of a faux bold.
  instrument: {
    label: 'Instrument', display: "'Instrument Serif', serif", body: "'Inter', sans-serif",
    href: G('family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600'),
    regular: true,
  },
  // Lato: the humanist sans of the classic long-form sales letter (900 heads).
  lato: {
    label: 'Lato', display: "'Lato', sans-serif", body: "'Lato', sans-serif",
    href: G('family=Lato:ital,wght@0,400;0,700;0,900;1,400;1,700'),
  },
  // Outfit: a soft, friendly geometric sans for warm, playful pages.
  outfit: {
    label: 'Outfit', display: "'Outfit', sans-serif", body: "'Outfit', sans-serif",
    href: G('family=Outfit:wght@400;500;600;700;800'),
  },
};

export function fontFor(font: PageFont | undefined): FontPair | null {
  return font ? FONTS[font] ?? null : null;
}

// Order shown in the editor's Font picker.
export const PAGE_FONTS: PageFont[] = ['jakarta', 'inter', 'manrope', 'poppins', 'outfit', 'lato', 'sora', 'space', 'bricolage', 'oswald', 'figtree', 'instrument', 'playfair', 'fraunces', 'cormorant'];
